import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { historyAPI } from '../utils/api';
import { History, Clock, Terminal, FolderOpen, RefreshCw } from 'lucide-react';

export default function HistoryPage() {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [serialLog, setSerialLog] = useState([]);
  const [tab, setTab] = useState('config');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [histRes, serialRes] = await Promise.all([
        historyAPI.list(),
        historyAPI.getSerialLog(),
      ]);
      setHistory(histRes.data.items || []);
      setSerialLog(serialRes.data || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[--text-primary] flex items-center gap-3">
            <History className="w-7 h-7 text-amber-400" />
            {t('nav.history')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">Configuration changes and serial command log</p>
        </div>
        <button onClick={loadData} className="btn-ghost flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('config')}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all
            ${tab === 'config' ? 'bg-brand-500/10 text-brand-400 border border-brand-500/30' : 'text-gray-500 hover:text-gray-300'}`}
        >
          <FolderOpen className="w-4 h-4" />
          Config Changes
        </button>
        <button
          onClick={() => setTab('serial')}
          className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all
            ${tab === 'serial' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/30' : 'text-gray-500 hover:text-gray-300'}`}
        >
          <Terminal className="w-4 h-4" />
          Serial Commands
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" />
        </div>
      ) : tab === 'config' ? (
        <div className="space-y-2">
          {history.length === 0 ? (
            <div className="card p-12 text-center">
              <Clock className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No configuration changes yet</p>
            </div>
          ) : (
            history.map((item) => (
              <div key={item.id} className="card p-4 flex items-center gap-4">
                <div className="w-2 h-2 rounded-full bg-brand-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="badge-blue">{item.action}</span>
                    {item.profileName && <span className="text-sm text-gray-400">{item.profileName}</span>}
                  </div>
                  {item.details && (
                    <p className="text-xs text-gray-600 font-mono mt-1 truncate">
                      {JSON.stringify(item.details)}
                    </p>
                  )}
                </div>
                <span className="text-xs text-gray-600 shrink-0">
                  {new Date(item.createdAt).toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {serialLog.length === 0 ? (
            <div className="card p-12 text-center">
              <Terminal className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500">No serial commands logged yet</p>
            </div>
          ) : (
            serialLog.map((item) => (
              <div key={item.id} className="card p-4 flex items-center gap-4">
                <div className={`w-2 h-2 rounded-full shrink-0 ${item.success ? 'bg-green-500' : 'bg-red-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="hex-display text-xs">{item.commandHex}</div>
                  {item.description && <p className="text-xs text-gray-500 mt-1">{item.description}</p>}
                </div>
                <span className="text-xs text-gray-600 shrink-0">
                  {new Date(item.createdAt).toLocaleString()}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
